import React, { useEffect, useMemo, useRef } from 'react';
import { VenueApiResult } from '../../../types/venue';

interface VenueMarkerProps {
  venue: VenueApiResult;
  map: google.maps.Map | null;
  theme: 'dark' | 'light';
  onMarkerClick?: (venueId: string) => void;
  isSelected?: boolean;
}

const VenueMarker: React.FC<VenueMarkerProps> = ({
  venue,
  map,
  onMarkerClick,
  isSelected,
  theme
}) => {
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  const position = useMemo(() => {
    if (!venue.coordinates) return null;
    return new google.maps.LatLng(venue.coordinates.lat, venue.coordinates.lng);
  }, [venue.coordinates]);

  useEffect(() => {
    if (!map || !position) return;

    class MarkerOverlay extends google.maps.OverlayView {
      div: HTMLDivElement | null = null;

      onAdd() {
        this.div = document.createElement('div');
        this.div.className = 'relative';
        this.div.style.position = 'absolute';
        this.div.style.zIndex = isSelected ? '9999' : '1';

        const wrapper = document.createElement('div');
        wrapper.className = 'relative w-10 h-10 flex items-center justify-center';

        // Double-ring pulse when selected
        if (isSelected) {
          const ring1 = document.createElement('div');
          ring1.className = 'absolute w-10 h-10 rounded-full bg-yellow-400 opacity-60 animate-ping z-0';

          const ring2 = document.createElement('div');
          ring2.className = 'absolute w-14 h-14 rounded-full bg-yellow-300 opacity-30 animate-ping z-0';
          ring2.style.animationDelay = '200ms';

          wrapper.appendChild(ring2);
          wrapper.appendChild(ring1);
        }

        // Tooltip
        const tooltip = document.createElement('div');
        tooltip.innerText = venue.name;
        tooltip.className =
          'absolute bottom-full mb-2 px-2 py-1 rounded text-xs text-white bg-black shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30';
        wrapper.appendChild(tooltip);

        // Central dot / logo
        const dot = document.createElement('div');
        dot.className = `
          relative z-10 w-8 h-8 rounded-full overflow-hidden border-2 
          ${theme === 'dark' ? 'border-white' : 'border-black'}
          ${isSelected ? 'bg-white' : 'bg-yellow-400'}
          shadow-md
          transition-transform duration-200 ease-in-out
          ${isSelected ? 'scale-110' : 'scale-100'}
        `;

        if (venue.logo) {
          const img = document.createElement('img');
          img.src = venue.logo;
          img.alt = venue.name;
          img.className = 'w-full h-full object-cover';
          dot.appendChild(img);
        }

        const group = document.createElement('div');
        group.className = 'group relative cursor-pointer';
        group.appendChild(dot);
        wrapper.appendChild(group);

        wrapper.addEventListener('click', () => {
          if (onMarkerClick) onMarkerClick(venue.id);
        });

        this.div.appendChild(wrapper);

        const panes = this.getPanes();
        if (panes) {
          panes.overlayMouseTarget.appendChild(this.div);
        }
      }

      draw() {
        if (!this.div || !position) return;
        const projection = this.getProjection();
        const point = projection.fromLatLngToDivPixel(position);
        if (point) {
          this.div.style.left = `${point.x - 20}px`;
          this.div.style.top = `${point.y - 20}px`;
          this.div.style.zIndex = isSelected ? '9999' : '1';
        }
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
        this.div = null;
      }
    }

    const overlay = new MarkerOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
    };
  }, [map, position, isSelected, theme]);

  return null;
};

export default VenueMarker;
